import axiosInstance from "@/api/axios-instance";
import type {
  FeedbackSubmitKind,
  FeedbackStatus,
  HandbookDoc,
  HandbookEditorPage,
  HandbookFeedback,
  HandbookHelpEntry,
  HandbookManageTree,
  HandbookOwner,
  HandbookPageKind,
  HandbookPublishedPage,
  HandbookRevisionEntry,
  HandbookSearchEntry,
  HandbookSummary,
  HandbookTree,
  PublishIssue,
} from "../types";

/** Thin calls to /v1/handbook (readers) and /v1/handbook/manage (editors). */

// ── readers ──

export const getHandbookSummary = async (): Promise<HandbookSummary> =>
  (await axiosInstance.get("/handbook/summary")).data;

export const getHandbookTree = async (): Promise<HandbookTree> =>
  (await axiosInstance.get("/handbook/tree")).data;

export const getHandbookPage = async (slug: string): Promise<HandbookPublishedPage> =>
  (await axiosInstance.get(`/handbook/pages/${encodeURIComponent(slug)}`)).data;

export const getHandbookSearchIndex = async (): Promise<HandbookSearchEntry[]> =>
  (await axiosInstance.get("/handbook/search-index")).data;

export const getHandbookHelp = async (area: string): Promise<HandbookHelpEntry[]> =>
  (await axiosInstance.get("/handbook/help", { params: { area } })).data;

export const createHandbookFeedback = async (
  pageId: string,
  body: { kind: FeedbackSubmitKind; message?: string; section_id?: string; page_version?: number },
) => (await axiosInstance.post(`/handbook/pages/${pageId}/feedback`, body)).data;

// ── editors ──

export const getManageTree = async (): Promise<HandbookManageTree> =>
  (await axiosInstance.get("/handbook/manage/tree")).data;

export const getHandbookOwners = async (): Promise<HandbookOwner[]> =>
  (await axiosInstance.get("/handbook/manage/owners")).data;

export const createChapter = async (body: {
  slug: string;
  title: string;
  description?: string;
}) => (await axiosInstance.post("/handbook/manage/chapters", body)).data;

export const updateChapter = async (
  id: string,
  body: { title?: string; description?: string },
) => (await axiosInstance.patch(`/handbook/manage/chapters/${id}`, body)).data;

export const deleteChapter = async (id: string) =>
  (await axiosInstance.delete(`/handbook/manage/chapters/${id}`)).data;

export const reorderChapters = async (ids: string[]) =>
  (await axiosInstance.post("/handbook/manage/chapters/reorder", { ids })).data;

export const reorderPages = async (chapterId: string, ids: string[]) =>
  (await axiosInstance.post(`/handbook/manage/chapters/${chapterId}/pages/reorder`, { ids }))
    .data;

export const createPage = async (body: {
  chapter_id: string;
  slug: string;
  title: string;
  kind: HandbookPageKind;
  summary?: string;
}): Promise<HandbookEditorPage> =>
  (await axiosInstance.post("/handbook/manage/pages", body)).data;

export const getEditorPage = async (id: string): Promise<HandbookEditorPage> =>
  (await axiosInstance.get(`/handbook/manage/pages/${id}`)).data;

export interface SavePageBody {
  expected_version: number;
  title?: string;
  summary?: string;
  kind?: HandbookPageKind;
  areas?: string[];
  content?: HandbookDoc;
  chapter_id?: string;
  slug?: string;
  owner_internal_user_id?: string;
  change_note?: string;
}

export const saveEditorPage = async (
  id: string,
  body: SavePageBody,
): Promise<HandbookEditorPage> =>
  (await axiosInstance.patch(`/handbook/manage/pages/${id}`, body)).data;

export const publishEditorPage = async (
  id: string,
  body: {
    expected_version: number;
    change_note: string;
    reviewed_whole_page?: boolean;
    acknowledge_warnings?: boolean;
  },
): Promise<{ page: HandbookEditorPage; warnings: PublishIssue[] }> =>
  (await axiosInstance.post(`/handbook/manage/pages/${id}/publish`, body)).data;

export const discardEditorChanges = async (
  id: string,
  expected_version: number,
): Promise<HandbookEditorPage> =>
  (await axiosInstance.post(`/handbook/manage/pages/${id}/discard`, { expected_version }))
    .data;

export const setPageArchived = async (
  id: string,
  archived: boolean,
  expected_version: number,
): Promise<HandbookEditorPage> =>
  (
    await axiosInstance.post(
      `/handbook/manage/pages/${id}/${archived ? "archive" : "unarchive"}`,
      { expected_version },
    )
  ).data;

export const markPageReviewed = async (
  id: string,
  expected_version: number,
  note?: string,
): Promise<HandbookEditorPage> =>
  (await axiosInstance.post(`/handbook/manage/pages/${id}/review`, { expected_version, note })).data;

export const getPageRevisions = async (id: string): Promise<HandbookRevisionEntry[]> =>
  (await axiosInstance.get(`/handbook/manage/pages/${id}/revisions`)).data;

export const getHandbookFeedback = async (
  status?: FeedbackStatus,
): Promise<HandbookFeedback[]> =>
  (await axiosInstance.get("/handbook/manage/feedback", { params: status ? { status } : {} }))
    .data;

export const updateHandbookFeedback = async (
  id: string,
  body: { status: FeedbackStatus; resolution_note?: string },
) => (await axiosInstance.patch(`/handbook/manage/feedback/${id}`, body)).data;
